/**
 * PAPPO — CoGuardians
 * Cônjuge / guarda compartilhada: adicionar um segundo RESPONSÁVEL a um filho.
 * O co-responsável precisa ser Nível 01 e aceitar o convite; ao aceitar, ganha
 * poderes iguais ao principal (vê conversas + recebe alertas + gere amizades).
 * Renderizado na ContactsPage apenas para parent/admin.
 */

import { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, Check, XCircle, UserMinus } from 'lucide-react';
import { Button } from '../common/Button';
import {
  listCoGuardians,
  inviteCoGuardian,
  acceptCoGuardian,
  rejectCoGuardian,
  removeCoGuardian,
  type CoGuardiansData,
} from '../../services/parent';
import styles from './family.module.css';

export function CoGuardians() {
  const [data, setData] = useState<CoGuardiansData>({
    incoming: [],
    outgoing: [],
    children: [],
  });
  const [emailByChild, setEmailByChild] = useState<Record<number, string>>({});
  const [feedback, setFeedback] = useState('');

  const load = useCallback(async () => {
    const res = await listCoGuardians();
    if (res.success && res.data) setData(res.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const invite = async (childId: number) => {
    const email = (emailByChild[childId] ?? '').trim();
    if (!email) {
      setFeedback('Informe o email do co-responsável.');
      return;
    }
    const res = await inviteCoGuardian(childId, email);
    setFeedback(
      res.success ? 'Convite enviado! A pessoa precisa aceitar.' : res.error ?? 'Erro.'
    );
    if (res.success) {
      setEmailByChild((p) => ({ ...p, [childId]: '' }));
      await load();
    }
  };
  const accept = async (id: number) => {
    const res = await acceptCoGuardian(id);
    setFeedback(res.success ? 'Você agora é co-responsável.' : res.error ?? 'Erro.');
    await load();
  };
  const reject = async (id: number) => {
    await rejectCoGuardian(id);
    await load();
  };
  const remove = async (childId: number, targetId: number, isMe: boolean) => {
    const res = await removeCoGuardian(childId, targetId);
    setFeedback(
      res.success
        ? isMe
          ? 'Você saiu como responsável.'
          : 'Co-responsável removido.'
        : res.error ?? 'Erro.'
    );
    await load();
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.icon}>
          <ShieldCheck size={20} />
        </span>
        <div>
          <h3 className={styles.title}>Co-responsáveis</h3>
          <p className={styles.subtitle}>
            Compartilhe um filho com outro adulto — cônjuge ou guarda compartilhada.
          </p>
        </div>
      </div>

      {/* Como funciona */}
      <div className={styles.steps}>
        <div className={styles.step}>
          <span className={styles.stepNum}>1</span>
          <span className={styles.stepText}>
            O outro adulto cria a conta e verifica a identidade (Nível 01).
          </span>
        </div>
        <div className={styles.step}>
          <span className={styles.stepNum}>2</span>
          <span className={styles.stepText}>
            Você convida por email, no filho que quer compartilhar.
          </span>
        </div>
        <div className={styles.step}>
          <span className={styles.stepNum}>3</span>
          <span className={styles.stepText}>
            Ele aceita e passa a ver as conversas e receber os alertas.
          </span>
        </div>
      </div>

      {data.children.length === 0 ? (
        <p className={styles.empty}>
          Crie um perfil de filho no Painel Parental para compartilhar a
          responsabilidade com outro adulto.
        </p>
      ) : (
        data.children.map((c) => (
          <div key={c.child_id} className={styles.childBlock}>
            <p className={styles.childName}>{c.child_name}</p>
            <div className={styles.chips}>
              {c.guardians.map((g) => (
                <span key={g.id} className={styles.chip}>
                  {g.name}
                  {g.is_me ? ' (você)' : ''}
                  {g.removable && (
                    <button
                      type="button"
                      title={g.is_me ? 'Sair como responsável' : 'Remover'}
                      className={styles.chipRemove}
                      onClick={() => remove(c.child_id, g.id, g.is_me)}
                    >
                      <UserMinus size={13} />
                    </button>
                  )}
                </span>
              ))}
            </div>
            <div className={styles.field}>
              <input
                className={`${styles.select} ${styles.grow}`}
                type="email"
                placeholder="email@do-outro-responsavel.com"
                value={emailByChild[c.child_id] ?? ''}
                onChange={(e) =>
                  setEmailByChild((p) => ({ ...p, [c.child_id]: e.target.value }))
                }
              />
              <Button size="sm" onClick={() => invite(c.child_id)}>
                Convidar
              </Button>
            </div>
          </div>
        ))
      )}

      {data.incoming.length > 0 && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Convites recebidos ({data.incoming.length})</p>
          {data.incoming.map((r) => (
            <div key={r.id} className={styles.request}>
              <span className={styles.requestText}>
                <strong>{r.from_parent_name}</strong> convidou você para ser
                co-responsável de <strong>{r.child_name}</strong>.
              </span>
              <Button size="sm" leftIcon={<Check size={14} />} onClick={() => accept(r.id)}>
                Aceitar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<XCircle size={14} />}
                onClick={() => reject(r.id)}
              >
                Recusar
              </Button>
            </div>
          ))}
        </div>
      )}

      {data.outgoing.length > 0 && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Convites enviados</p>
          {data.outgoing.map((o) => (
            <div key={o.id} className={styles.request}>
              <span className={styles.requestText}>
                {o.to_parent_name} · {o.child_name}
              </span>
              <span className={styles.muted}>aguardando</span>
            </div>
          ))}
        </div>
      )}

      {feedback && <p className={styles.feedback}>{feedback}</p>}
    </div>
  );
}
